import type { PrivateUserInfoType } from '@otw/models'
import { salesReps } from '@/constants'
import { getUserDisplayName } from 'hooks/useUsers'

/**
 * Resolve a contact/company owner id to a display name.
 * Portal users are primary; the legacy `salesReps` constant is only a fallback
 * for older numeric/string rep ids that are not portal accounts.
 */
export const resolveOwnerDisplayName = (
  ownerId: string | number | null | undefined,
  users: PrivateUserInfoType[],
  unknownLabel = 'Unknown',
): string => {
  if (ownerId === null || ownerId === undefined || ownerId === '') {
    return 'Unassigned'
  }

  const key = String(ownerId)
  const portalUser = users.find((user) => String(user.id) === key)
  if (portalUser) return getUserDisplayName(portalUser)

  const rep = salesReps.find((candidate) => String(candidate.id) === key)
  if (rep) {
    const name = `${rep.first_name} ${rep.last_name}`.trim()
    if (name) return name
  }

  return unknownLabel
}

/** Build an id → display-name index (portal users first, salesReps as fallback). */
export const buildOwnerDisplayNameIndex = (
  users: PrivateUserInfoType[],
): Map<string, string> => {
  const map = new Map<string, string>()
  for (const user of users) {
    map.set(String(user.id), getUserDisplayName(user))
  }
  for (const rep of salesReps) {
    const key = String(rep.id)
    if (map.has(key)) continue
    const name = `${rep.first_name} ${rep.last_name}`.trim()
    if (name) map.set(key, name)
  }
  return map
}
